// BeneAIComposer — BeneAI-01.
//
// Textarea + send button. Enter sends, Shift+Enter for a newline. The
// composer never owns the in-flight network state — the parent passes
// ``isSending`` and ``onSend`` so the same composer flows through any
// future drawer shell (full page, modal, etc.).

import { Send } from "lucide-react";
import {
  useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent,
} from "react";


interface BeneAIComposerProps {
  isSending: boolean;
  /** Called when the composer wants to send. Returns the value the
   *  textarea should restore to (e.g. the original draft on error). */
  onSend: (body: string) => Promise<void> | void;
  /** Hint copy under the composer (e.g. surfaced error). */
  helperText?: string;
  /** Renders helperText in the danger style when set. */
  helperTone?: "info" | "error";
  /** Programmatically focus the composer on mount (drawer open). */
  autoFocus?: boolean;
}


export function BeneAIComposer({
  isSending, onSend, helperText, helperTone = "info", autoFocus = false,
}: BeneAIComposerProps) {
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const id = window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [autoFocus]);

  const submit = useCallback(async () => {
    const body = value.trim();
    if (body.length === 0 || isSending) return;
    setValue("");
    await onSend(body);
  }, [onSend, value, isSending]);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void submit();
    },
    [submit],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        void submit();
      }
    },
    [submit],
  );

  return (
    <form
      data-testid="beneai-composer"
      onSubmit={handleSubmit}
      className="border-t border-border-subtle bg-canvas px-4 py-3"
    >
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          data-testid="beneai-composer-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          placeholder="Ask BeneAI…"
          disabled={isSending}
          className="min-h-[40px] max-h-40 flex-1 resize-none rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm text-text-primary outline-none focus:border-teal focus:ring-1 focus:ring-teal disabled:opacity-60"
        />
        <button
          type="submit"
          data-testid="beneai-composer-send"
          aria-label="Send message"
          disabled={isSending || value.trim().length === 0}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-teal text-text-inverse transition-fast ease-out hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send size={16} aria-hidden="true" />
        </button>
      </div>
      {helperText && (
        <div
          data-testid="beneai-composer-helper"
          data-tone={helperTone}
          className={[
            "mt-2 text-xs",
            helperTone === "error"
              ? "text-ember"
              : "text-text-tertiary",
          ].join(" ")}
        >
          {helperText}
        </div>
      )}
    </form>
  );
}
